import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import PromotionGrid from "@/pages/admin/promociones/components/PromotionGrid";
import PromotionFormModal from "@/pages/admin/promociones/components/PromotionFormModal";
import { formatNumber } from "@/utils/format";

const PromotionsPage = () => {
  const { data, addPromotion, deletePromotion } = useApp();
  const [formOpen, setFormOpen] = useState(false);
  const [feedback, setFeedback] = useState("");

  const stats = useMemo(() => {
    const active = data.promotions.filter((p) => new Date(p.endDate) >= new Date());
    const avgDiscount = data.promotions.length
      ? Math.round(
          data.promotions.reduce((acc, p) => acc + p.discountPct, 0) / data.promotions.length
        )
      : 0;
    const bestDiscount = data.promotions.reduce((acc, p) => Math.max(acc, p.discountPct), 0);
    return { active: active.length, avgDiscount, bestDiscount, total: data.promotions.length };
  }, [data.promotions]);

  const handleCreate = async (p: Parameters<typeof addPromotion>[0]) => {
    await addPromotion(p);
    setFormOpen(false);
    setFeedback("Promoción publicada y difundida a tu red de asesores.");
    setTimeout(() => setFeedback(""), 2800);
  };

  const handleDelete = async (id: string) => {
    await deletePromotion(id);
    setFeedback("Promoción eliminada.");
    setTimeout(() => setFeedback(""), 2200);
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Promociones y Combos"
        subtitle="Diseña ofertas masivas y difúndelas automáticamente a la app de tus asesores."
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        {[
          { icon: "ri-megaphone-line", label: "Promociones totales", value: formatNumber(stats.total), bubble: "bg-primary-500/25 text-primary-100" },
          { icon: "ri-flashlight-line", label: "Activas ahora", value: formatNumber(stats.active), bubble: "bg-accent-500/20 text-accent-200" },
          { icon: "ri-percent-line", label: "Descuento promedio", value: `${stats.avgDiscount}%`, bubble: "bg-secondary-500/25 text-secondary-200" },
          { icon: "ri-fire-line", label: "Mejor descuento", value: `${stats.bestDiscount}%`, bubble: "bg-accent-500/15 text-accent-100" },
        ].map((s) => (
          <div key={s.label} className="glass-panel rounded-lg p-4 animate-fade-up">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.bubble}`}>
              <i className={`${s.icon} text-lg`} />
            </span>
            <p className="text-[11px] text-foreground-500 mt-3">{s.label}</p>
            <p className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5 mb-4">
        <p className="text-xs text-foreground-600">
          Toda promoción publicada notifica al instante a los asesores de tu red.
        </p>
        <button
          onClick={() => setFormOpen(true)}
          className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
        >
          <i className="ri-add-line" /> Nueva promoción
        </button>
      </div>

      {feedback && (
        <div className="mb-3 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      <PromotionGrid promotions={data.promotions} onDelete={handleDelete} />

      <PromotionFormModal
        open={formOpen}
        products={data.products}
        inventory={data.inventory}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />
    </div>
  );
};

export default PromotionsPage;